import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSignature } from "react-icons/fa";
import { LuEraser, LuLock, LuPenLine, LuSave, LuStar, LuTrash2, LuUpload } from "react-icons/lu";
import { Button, confirm } from "components/common";
import SignaturePad from "./SignaturePad";
import {
  listMySignatures, createMySignature, setDefaultMySignature, deleteMySignature,
} from "./signatureApi";

// Quản lý chữ ký Hub-Sign ngay trong Portal (thay iframe /sign-hub/manage — iframe bị
// CSP frame-ancestors của Hub-Sign chặn và token Portal không verify được chéo môi
// trường). Portal API proxy S2S sang Hub-Sign nên chạy được ở mọi môi trường.
// Dùng chung cho tab "My signature" của Payment Request và Purchase.
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

const errorText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

const readFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const MySignatureManager = ({ description }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [mode, setMode] = useState("draw");
  const [padEmpty, setPadEmpty] = useState(true);
  const [upload, setUpload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const padRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listMySignatures();
      setSignatures(res.data || []);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err, "Cannot load your signatures."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    padRef.current?.clear();
    setUpload(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Please choose a PNG or JPEG image.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("Image must be 2 MB or smaller.");
      e.target.value = "";
      return;
    }
    setUpload(await readFile(file));
  };

  const image = mode === "draw" ? (padEmpty ? null : "pad") : upload;

  const save = async () => {
    const dataUrl = mode === "draw" ? padRef.current?.toDataURL() : upload;
    if (!dataUrl) return;
    setSaving(true);
    try {
      await createMySignature({ image: dataUrl, makeDefault: signatures.length === 0 });
      toast.success("Signature saved. You can now sign requests — in the Portal or straight from the email.");
      resetForm();
      await load();
    } catch (err) {
      toast.error(errorText(err, "Cannot save the signature."));
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (sig) => {
    setBusyId(sig.id);
    try {
      const res = await setDefaultMySignature(sig.id);
      setSignatures(res.data || []);
    } catch (err) {
      toast.error(errorText(err, "Cannot change the default signature."));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (sig) => {
    const ok = await confirm({
      title: "Delete this signature?",
      message: "It will no longer be offered when you sign.",
      type: "danger",
      confirmText: "Delete",
    });
    if (!ok) return;
    setBusyId(sig.id);
    try {
      const res = await deleteMySignature(sig.id);
      setSignatures(res.data || []);
    } catch (err) {
      toast.error(errorText(err, "Cannot delete the signature."));
    } finally {
      setBusyId(null);
    }
  };

  const tabCls = (on) => `tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-geist-sm tw-text-xs tw-font-medium tw-border-0 tw-cursor-pointer ${
    on ? "tw-bg-white tw-text-geist-fg tw-shadow-geist-card" : "tw-bg-transparent tw-text-geist-fg-3 hover:tw-text-geist-fg"
  }`;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaSignature className="tw-text-geist-accent" />
          <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">My signature</span>
        </div>
        <p className="tw-text-xs tw-text-geist-fg-3 tw-m-0 tw-mt-0.5 tw-max-w-lg">{description}</p>
      </div>

      {/* Chữ ký đã lưu */}
      <div className="tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-bg-white tw-p-4">
        <div className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3 tw-mb-3">Saved signatures</div>
        {loading && (
          <div className="tw-text-sm tw-text-geist-fg-3"><i className="bi bi-arrow-repeat tw-animate-spin tw-mr-2" />Loading…</div>
        )}
        {!loading && loadError && (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-[#8e2a18]">
            <span>{loadError}</span>
            <Button variant="secondary" size="sm" onClick={load}>Retry</Button>
          </div>
        )}
        {!loading && !loadError && signatures.length === 0 && (
          <p className="tw-text-sm tw-text-geist-fg-3 tw-m-0">
            You have no signature yet. Draw or upload one below — the first one becomes your default.
          </p>
        )}
        {!loading && !loadError && signatures.length > 0 && (
          <div className="tw-grid tw-gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {signatures.map((sig) => (
              <div key={sig.id}
                className={`tw-flex tw-flex-col tw-rounded-geist-md tw-border tw-border-solid tw-p-3 ${
                  sig.is_default ? "tw-border-geist-accent tw-bg-geist-accent/5" : "tw-border-geist-border"
                }`}>
                <div className="tw-flex tw-h-20 tw-items-center tw-justify-center tw-bg-white tw-rounded-geist-sm">
                  {sig.image_url
                    ? <img src={sig.image_url} alt="Signature" className="tw-max-h-full tw-max-w-full tw-object-contain" />
                    : <span className="tw-text-xs tw-text-geist-fg-3">Preview unavailable</span>}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2 tw-text-[11px] tw-text-geist-fg-3">
                  {sig.is_default && <span className="tw-font-semibold tw-text-geist-accent">Default</span>}
                  {sig.is_locked && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1" title="Already used to sign documents — kept for the record">
                      <LuLock size={10} /> Used {sig.usage_count}×
                    </span>
                  )}
                </div>
                <div className="tw-flex tw-gap-2 tw-mt-2">
                  {!sig.is_default && (
                    <Button variant="secondary" size="xs" leftIcon={LuStar} loading={busyId === sig.id}
                      onClick={() => makeDefault(sig)}>Make default</Button>
                  )}
                  {!sig.is_locked && (
                    <Button variant="ghost" size="xs" leftIcon={LuTrash2} disabled={busyId === sig.id}
                      onClick={() => remove(sig)}>Delete</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tạo chữ ký mới */}
      <div className="tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-bg-white tw-p-4">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-3">
          <div className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3">Add a signature</div>
          <div className="tw-inline-flex tw-gap-1 tw-p-1 tw-rounded-geist-md tw-bg-geist-bg-2">
            <button type="button" className={tabCls(mode === "draw")} onClick={() => setMode("draw")}><LuPenLine size={12} /> Draw</button>
            <button type="button" className={tabCls(mode === "upload")} onClick={() => setMode("upload")}><LuUpload size={12} /> Upload image</button>
          </div>
        </div>

        {/* Giữ canvas trong DOM khi đổi tab để không mất nét đang vẽ. */}
        <div style={{ display: mode === "draw" ? "block" : "none" }}>
          <SignaturePad ref={padRef} onChange={setPadEmpty} />
          <p className="tw-text-[11px] tw-text-geist-fg-3 tw-m-0 tw-mt-1.5">Sign inside the box with your mouse, trackpad or finger.</p>
        </div>

        {mode === "upload" && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onPickFile} className="tw-text-xs" />
            <p className="tw-text-[11px] tw-text-geist-fg-3 tw-m-0">PNG or JPEG, up to 2 MB. A transparent PNG prints best.</p>
            {upload && (
              <div className="tw-flex tw-h-28 tw-items-center tw-justify-center tw-rounded-geist-md tw-border tw-border-dashed tw-border-geist-border-2 tw-bg-white">
                <img src={upload} alt="Signature preview" className="tw-max-h-full tw-max-w-full tw-object-contain" />
              </div>
            )}
          </div>
        )}

        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-3">
          <Button variant="secondary" size="sm" leftIcon={LuEraser} disabled={saving || !image} onClick={resetForm}>Clear</Button>
          <Button variant="primary" size="sm" leftIcon={LuSave} loading={saving} disabled={!image} onClick={save}>
            Save signature
          </Button>
        </div>
      </div>
    </div>
  );
};

MySignatureManager.propTypes = {
  description: PropTypes.node,
};

export default MySignatureManager;
